﻿import React, { Fragment, useState, useEffect } from 'react';
import { FormTextInput, FormMultiSelect, FormSwitch, FormPagedCheckboxList } from "components/form-fields";
import { ShowHide } from "components/layout";
import { useValidation } from "hooks/useValidation";
import PropTypes from 'prop-types';
import { EmployeeService, LocationService } from "services";
import { Intent } from '@blueprintjs/core';
import { NotificationToaster } from "components/notifications";
import { GridLayout } from 'components/grid';
import 'components/settings/settings.css';

export function PolicyDetails(
    {
        disabled = false,
        saving = false,
        reoccurrenceSettingsDisabled = false,
        onSettingsUpdate = () => { },
        onValidationUpdate = () => { },
        editing,
        loading,
        policy,
        ...props
    }
) {
    const [isValid, errors, validate] = useValidation();
    const [loadingValue, setLoading] = useState(true);
    const [policySettings, setPolicySettings] = useState(
        {
            name: "",
            endDate: "",
            employeeCategorys: [],
            employeeTypes: [],
            locations: [],
            resendSchedule: {
                reoccurring: false
            },
            useAllLocations: true,
            sendDebriefAlert: true,
            debriefAlertDelayDays: 0
        });
    const [driverTypes, setDriverTypes] = useState([]);
    const [driverCategories, setDriverCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [totalLocationRecordCount, setTotalLocationRecordCount] = useState(0);
    const [loadingLocations, setLoadingLocations] = useState(true);

    const maxNameLength = 255;

    const locationFilter = {
        "filters":
            [
                { "key": "BusinessAreas", "value": "Tacho" },
                { "key": "States", "value": "Active" }
            ]
    };

    useEffect(initialLoad, []);

    function initialLoad() {
        EmployeeService.getEmployeeOptions().then(function (employeeOptions) {
            setDriverTypes(employeeOptions.employeeTypes);
            setDriverCategories(employeeOptions.employeeCategories);
            setLoading(false);
        },
            () => {
                NotificationToaster.show(Intent.DANGER, "Could not load settings. Please refresh the page.");
            }
        );

    }
    useEffect(onValidationChange, [isValid]);

    function onValidationChange() {
        onValidationUpdate(isValid);
    }

    useEffect(() => {
        setPolicySettings(policy);
    }, [policy]);

    useEffect(() => {
        setLoading(loading);
    }, [loading]);

    function onLocationsPageChange(listSize, pageNumber, debouncedSearchTerm) {
        setLoadingLocations(true);
        LocationService.getLocationList(listSize, pageNumber, debouncedSearchTerm, 'Name', 'A', locationFilter).then(pagedLocations => {
            setLocations(pagedLocations.data);
            setTotalLocationRecordCount(pagedLocations.totalCount);
            setLoadingLocations(false);
        }, (error) => {
            NotificationToaster.show(Intent.DANGER, "Could not load settings. Please refresh the page.");
        });
    }

    function onNameChange(event) {

        var updatedPolicySettings = {
            ...policySettings,
            name: event.target.value
        }
        setPolicySettings(updatedPolicySettings);
        onSettingsUpdate(updatedPolicySettings);
    }

    function hasValue(item, array) {
        return array.some(function (x) {
            return x.id === item.id
        });
    }

    function onChange(item, listName) {
        var temp = [...policySettings[listName]];

        if (hasValue(item, temp)) {
            return;
        }
        temp.push(item);
        var updatedPolicySettings = {
            ...policySettings,
            [listName]: temp
        }
        setPolicySettings(updatedPolicySettings);
        onSettingsUpdate(updatedPolicySettings);
    }

    function onDriverCategoriesChange(item) {
        onChange(item, 'employeeCategorys');
    }

    function onDriverTypesChange(item) {
        onChange(item, 'employeeTypes');
    }

    function onLocationsChange(item, isChecked) {
        if (isChecked) {
            const locationsClone = [...policySettings.locations, item];

            onSettingsUpdate({
                ...policySettings,
                locations: locationsClone
            });
        } else {
            const selectedLocationIndex = policySettings.locations.findIndex(x => x.id === item.id);
            onRemove(selectedLocationIndex, 'locations');
        }
    }

    function onRemove(index, listName) {
        var temp = [...policySettings[listName]];
        temp.splice(index, 1);
        var updatedPolicySettings = {
            ...policySettings,
            [[listName]]: temp
        }
        setPolicySettings(updatedPolicySettings);
        onSettingsUpdate(updatedPolicySettings);
    }

    function onDriverCategoriesRemove(tag, index) {
        onRemove(index, 'employeeCategorys');
    }

    function onDriverTypesRemove(tag, index) {
        onRemove(index, 'employeeTypes');
    }

    function onuseAllLocationsChange(event) {
        policySettings.useAllLocations = event.target.checked;
        if (policySettings.useAllLocations) {
            policySettings.locations = [];
        }
        onSettingsUpdate(policySettings);
    }

    useEffect(updateValidationRules, [policy])

    function updateValidationRules() {
        let rules = [];

        if (!editing) {
            rules.push({ fieldName: "name", required: true, maxLength: maxNameLength });
            rules.push({ fieldName: "startDate", required: true });
            rules.push({ fieldName: "employeeCategorys", required: true });
            rules.push({ fieldName: "employeeTypes", required: true });

            if (props && policy && !policy.useAllLocations) {
                rules.push({ fieldName: "locations", required: true });
            }

            rules.push({ fieldName: "useAllLocations", required: true });
        }

        validate(rules, policy);
    }

    return (
        <Fragment>
            <GridLayout numberOfColumns={3} className="custom-grid-class">
                <div style={{ gridColumn: 'span 3' }}>
                    <FormTextInput
                        id="policy-name"
                        loading={loadingValue}
                        value={policySettings.name}
                        headingText="Policy name"
                        placeholder="Policy name"
                        onChange={onNameChange}
                        disabled={disabled || saving}      
                        fill={true}                  
                        dangerHelperText={errors.name} >
                    </FormTextInput>
                </div>
                <div style={{ gridColumn: 'span 1' }}>
                    <FormMultiSelect
                        id="driver-types"
                        headingText="Employee types"
                        items={driverTypes}
                        onItemSelect={onDriverTypesChange}
                        selectedValues={policySettings.employeeTypes}
                        dangerHelperText={errors.employeeTypes}
                        placeholder="Employee types"
                        loading={loadingValue}
                        disabled={disabled || saving}
                        onRemove={onDriverTypesRemove}
                    ></FormMultiSelect>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                    <FormMultiSelect
                        id="driver-categories"
                        headingText="Employee categories"
                        items={driverCategories}
                        onItemSelect={onDriverCategoriesChange}
                        selectedValues={policySettings.employeeCategorys}
                        dangerHelperText={errors.employeeCategorys}
                        placeholder="Employee categories"
                        loading={loadingValue}
                        disabled={disabled || saving}
                        onRemove={onDriverCategoriesRemove}
                        fill={true}
                    ></FormMultiSelect>
                </div>
                <div style={{ gridColumn: 'span 3' }}>
                    <FormSwitch
                        disabled={disabled || saving}
                        loading={loadingValue}
                        headingText="Apply to all locations"
                        checked={policySettings.useAllLocations}
                        onChange={onuseAllLocationsChange}>
                    </FormSwitch>
                    <ShowHide evaluator={!policySettings.useAllLocations}
                        show={(
                            <FormPagedCheckboxList
                                id="locations"
                                onPageChange={onLocationsPageChange}
                                headingText="Apply to the following locations:"
                                items={locations}
                                totalItemCount={totalLocationRecordCount}
                                onItemSelect={onLocationsChange}
                                selectedItems={policySettings.locations}
                                dangerHelperText={errors.locations}
                                placeholder="Search locations"
                                loading={loadingValue}
                                loadingPage={loadingLocations}
                                disabled={disabled}
                                saving={saving}
                                fill={true}
                            ></FormPagedCheckboxList>
                        )}>
                    </ShowHide>
                </div>
            </GridLayout>
        </Fragment>
    );
}

PolicyDetails.propTypes = {
    policy: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    saving: PropTypes.bool,
    reoccurrenceSettingsDisabled: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    onSettingsUpdate: PropTypes.func,
    onValidationUpdate: PropTypes.func.isRequired
};